import React from 'react';
import { Chip, Spinner } from './ui';
import { C } from '../data/constants';
import { useQuiz } from '../hooks/useQuiz';

export default function Quiz({ civKey, diffKey, onFinish, onExit, user }) {
  const {
    qNum, score, streak,
    timeLeft, timerPct, timerColor,
    loading, curQ, locked, picked, error,
    pickAnswer,
  } = useQuiz(civKey, diffKey, user?.id, onFinish);

  const diffLabel = { apprentice: 'Easy', scholar: 'Medium', sage: 'Hard' }[diffKey];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Top bar */}
      <div style={{ background: '#fff', padding: '10px 16px', borderBottom: `2px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={onExit} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted, padding: 0 }}>✕</button>
        {/* Progress bar */}
        <div style={{ flex: 1, height: 8, background: '#F0E8D8', borderRadius: 4, overflow: 'hidden', border: '2px solid #E0D0C0' }}>
          <div style={{ height: '100%', width: `${(qNum / 10) * 100}%`, background: `linear-gradient(90deg,${C.teal},${C.teal2})`, borderRadius: 3, transition: 'width .4s' }} />
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          {streak >= 2 && <Chip type="or">🔥 {streak}</Chip>}
          <Chip type="or">⭐ {score}</Chip>
        </div>
      </div>

      {/* Body */}
      <div className="scroll-area" style={{ padding: '14px 16px' }}>
        {/* Timer row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 50, height: 50, borderRadius: '50%', background: '#fff', border: `3px solid ${timerColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'border-color .3s' }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: C.text }}>{timeLeft}</span>
          </div>
          <div style={{ flex: 1, height: 8, background: '#F0E8D8', borderRadius: 4, overflow: 'hidden', border: '2px solid #E0D0C0' }}>
            <div style={{ height: '100%', width: `${timerPct * 100}%`, background: timerColor, borderRadius: 3, transition: 'width 1s linear, background .3s' }} />
          </div>
          <span style={{ fontSize: 11, color: C.muted, fontWeight: 700 }}>{qNum}/10</span>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '2.5rem 0' }}>
            <div className="wobble" style={{ fontSize: 40, marginBottom: 10 }}>🌍</div>
            <div style={{ fontSize: 13, color: C.muted }}><Spinner />Loading question…</div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ textAlign: 'center', padding: '1.5rem', color: C.coral, fontSize: 13 }}>⚠️ {error}</div>
        )}

        {/* Question */}
        {!loading && curQ && (
          <div className="fade-up">
            {/* Tags */}
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
              <Chip type="or">{curQ.category}</Chip>
              {curQ.era && <Chip type="bl">{curQ.era}</Chip>}
              <Chip type="tl">{diffLabel}</Chip>
            </div>

            {/* Question text */}
            <div style={{ fontSize: 17, fontWeight: 700, color: C.text, lineHeight: 1.45, marginBottom: 16 }}>
              {curQ.question}
            </div>

            {/* Options */}
            {curQ.options.map((opt, i) => {
              const isCorrect = locked && i === curQ.correct;
              const isWrong   = locked && i === picked && i !== curQ.correct;
              return (
                <div key={i} onClick={() => pickAnswer(i)} style={{
                  width: '100%', textAlign: 'left', padding: '13px 15px', marginBottom: 9,
                  borderRadius: 13, color: C.text, fontSize: 13, fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: 11,
                  cursor: locked ? 'default' : 'pointer',
                  background: isCorrect ? '#E8F8F0' : isWrong ? '#FFF0EC' : '#fff',
                  border: `2px solid ${isCorrect ? C.green : isWrong ? C.coral : C.border}`,
                  boxShadow: `0 2px 0 ${isCorrect ? '#1A7A40' : isWrong ? '#C03020' : C.border}`,
                  transition: 'all .12s',
                  pointerEvents: locked ? 'none' : 'auto',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700,
                    background: isCorrect ? C.green : isWrong ? C.coral : '#F5EFE5',
                    border: `2px solid ${isCorrect ? C.green : isWrong ? C.coral : C.border}`,
                    color: (isCorrect || isWrong) ? '#fff' : C.muted,
                  }}>
                    {'ABCD'[i]}
                  </div>
                  <span>{opt}</span>
                </div>
              );
            })}

            {/* Fact */}
            {locked && curQ.fact && (
              <div style={{ marginTop: 12, background: '#FFFBF0', border: '2px solid #FFD080', borderRadius: 14, padding: '12px 14px', display: 'flex', gap: 8 }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
                <div style={{ fontSize: 12, color: C.text, lineHeight: 1.65, fontWeight: 500 }}>{curQ.fact}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
